'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { Suspense } from 'react'
import { menuItems } from './Header'
import LinkedInIcon from '../../components/widgets/LinkedInIcon'
import { speakerData } from '@/data/speaker'
import { ThemeSwitcher } from '../../components/widgets/ThemeSwitcher'
import LanguageSwitcher from '../../components/widgets/LanguageSwitcher'

// Dynamically import dialogs to reduce initial JavaScript bundle size
const ImprintDialog = dynamic(() => import('../../components/widgets/ImprintDialog'), {
  ssr: false,
  loading: () => <span className="text-sm text-muted-foreground">Imprint</span>
})

const DataPrivacyDialog = dynamic(() => import('../../components/widgets/DataPrivacyDialog'), {
  ssr: false,
  loading: () => <span className="text-sm text-muted-foreground">Data Privacy</span>
})

const CopyrightSection = () => {
    return (
        <span className="text-muted-foreground block text-center text-sm py-4">
            © {new Date().getFullYear()}{' '}
            <Link 
                href={speakerData.companyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary duration-150"
            >
                {speakerData.company}
            </Link>
            , All rights reserved
        </span>
    )
}

interface FooterLinkProps {
    href: string;
    title: string;
}

const FooterLink = ({ href, title }: FooterLinkProps) => {
    return (
        <Link
            href={href}
            className="text-muted-foreground hover:text-primary block duration-150 text-base">
            <span>{title}</span>
        </Link>
    )
}

interface SocialLinkProps {
    href: string;
    ariaLabel: string;
    children: React.ReactNode;
}

const SocialLink = ({ href, ariaLabel, children }: SocialLinkProps) => {
    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaLabel}
            className="text-muted-foreground hover:text-primary block">
            {children}
        </Link>
    )
}

interface FooterSectionProps {
    locale: string;
}

export default function FooterSection({ locale }: FooterSectionProps) {
    const t = useTranslations('Navigation')

    return (
        <footer className="pt-8 pt-2 md:pt-6 md:pb-2">
            <div className="mx-auto max-w-5xl px-6">
                <div className="mx-auto flex items-center justify-center gap-4 size-fit">
                    <Link
                        href="/"
                        aria-label={t('home')}
                        className="block">
                        <span className="font-extrabold text-2xl uppercase text-gradient-brand">
                            {t('brandName')}
                        </span>
                    </Link>
                    <SocialLink
                        href={speakerData.socialLinks.linkedin}
                        ariaLabel={`LinkedIn profile of ${speakerData.name}`}>
                        <LinkedInIcon className="size-6" />
                    </SocialLink>
                </div>

                <div className="my-4 flex flex-wrap justify-center gap-8 text-base">
                    {menuItems.map((item) => (
                        <FooterLink key={item.id} href={item.href} title={t(item.translationKey)} />
                    ))}
                </div>

                <div className="text-muted-foreground my-2 flex items-center justify-center gap-2">
                    <ThemeSwitcher />
                    <LanguageSwitcher locale={locale} />
                </div>

                <div className="mt-1 mb-0 flex flex-wrap justify-center gap-4 text-base">
                    <div className="text-muted-foreground hover:text-primary block duration-150 text-base">
                        <ImprintDialog />
                    </div>
                    <div className="text-muted-foreground hover:text-primary block duration-150 text-base">
                        <DataPrivacyDialog />
                    </div>
                </div>

                <Suspense fallback={<span className="text-muted-foreground block text-center text-sm py-4">© {speakerData.company}</span>}>
                    <CopyrightSection />
                </Suspense>
                <span className="text-muted-foreground block text-center text-xs pb-4">
                    {t('lastUpdated')}
                </span>
            </div>
        </footer>
    )
}
